import { useEffect, useRef, useState } from "react";

/** Parse getComputedStyle backgroundColor (rgb / rgba). */
function parseCssBackgroundRgb(css: string): { r: number; g: number; b: number; a: number } | null {
  const t = css.trim();
  if (t === "transparent" || t === "") return null;
  const m = t.match(/rgba?\(\s*([\d.]+)\s*,\s*([\d.]+)\s*,\s*([\d.]+)(?:\s*,\s*([\d.]+))?\s*\)/i);
  if (!m) return null;
  const r = Number(m[1]);
  const g = Number(m[2]);
  const b = Number(m[3]);
  const a = m[4] !== undefined ? Number(m[4]) : 1;
  return { r, g, b, a };
}

/** WCAG relative luminance (0–1). */
function relativeLuminance(r: number, g: number, b: number): number {
  const lin = (c: number) => {
    const x = c / 255;
    return x <= 0.03928 ? x / 12.92 : Math.pow((x + 0.055) / 1.055, 2.4);
  };
  return 0.2126 * lin(r) + 0.7152 * lin(g) + 0.0722 * lin(b);
}

/**
 * true = nền tại điểm lấy mẫu tương đối tối → UI nên dùng nền sáng (trắng) cho nút.
 * Bỏ qua chính nút [data-back-to-top], đọc lớp phía dưới.
 */
function sampleIsDarkBehindFixedCorner(): boolean {
  const w = window.innerWidth;
  const h = window.innerHeight;
  const cx = Math.max(0, Math.round(w - 16 - 24));
  const cy = Math.max(0, Math.round(h - 16 - 24));

  try {
    const stack = document.elementsFromPoint(cx, cy);
    for (const node of stack) {
      if (!(node instanceof HTMLElement)) continue;
      if (node.dataset.backToTop === "true") continue;

      let el: HTMLElement | null = node;
      while (el) {
        const bg = getComputedStyle(el).backgroundColor;
        const p = parseCssBackgroundRgb(bg);
        if (p && p.a > 0.1) {
          return relativeLuminance(p.r, p.g, p.b) < 0.5;
        }
        el = el.parentElement;
      }
    }
  } catch {
    // ignore
  }
  return false;
}

/**
 * Theo dõi nền phía sau góc dưới-phải (vị trí nút back-to-top) khi `active`.
 */
export function useDarkBackgroundBehindDockCorner(active: boolean): boolean {
  const [isDark, setIsDark] = useState(false);
  const raf = useRef<number>(0);

  useEffect(() => {
    if (!active) return;

    const schedule = () => {
      if (raf.current) cancelAnimationFrame(raf.current);
      raf.current = requestAnimationFrame(() => {
        setIsDark(sampleIsDarkBehindFixedCorner());
      });
    };

    schedule();
    window.addEventListener("scroll", schedule, { passive: true });
    window.addEventListener("resize", schedule);

    return () => {
      window.removeEventListener("scroll", schedule);
      window.removeEventListener("resize", schedule);
      if (raf.current) cancelAnimationFrame(raf.current);
    };
  }, [active]);

  return isDark;
}
